import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CHANGE_EVENT_LOCAL_KEY,
  FEEDBACK_EVENT_ID,
  getDate,
  getMoment,
  getTitle,
  INTERNAL_DATE_FORMAT,
  typeToDisplayText,
  typeToHeaderClass,
  typeToTextColor,
} from "@/utils/agenda.ts";
import dayjs from "dayjs";
import { useToggle } from "react-use";
import { useUserEventsAndMaterials } from "@/data/user.ts";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { cn } from "@/utils/string.ts";

interface AgendaContextProps {
  previousWeek: () => void;
  nextWeek: () => void;
  today: () => void;
  week: string[];
  isCurrentWeek: boolean;
  isCurrentMonth: boolean;
  index: number;
  isMonthView: boolean;
  toggleMonthView: () => void;
  next: () => void;
  previous: () => void;
}

const AgendaContext = createContext<AgendaContextProps | undefined>(undefined);

export const AgendaProvider: React.FC<
  PropsWithChildren<NonNullable<unknown>>
> = ({ children }) => {
  const [isMonthView, toggleMonthView] = useToggle(true);
  const weeksRef = useRef(getDate(getMoment(0)));
  const [weekIndex, setWeekIndex] = useState(0); // create  index
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0); // create  index
  const [index, setIndex] = useState(0); // create  index

  const { eventMaterials } = useUserEventsAndMaterials();

  const { openModal } = useModalAction();

  useEffect(() => {
    const date = dayjs();
    weeksRef.current.forEach((week, idx) => {
      if (week.includes(date.format(INTERNAL_DATE_FORMAT))) {
        setWeekIndex(idx);
        setCurrentWeekIndex(idx);
        return;
      }
    });
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  const nextWeek = () => {
    const addEnd = weekIndex + 1;
    if (addEnd >= weeksRef.current.length - 1) {
      weeksRef.current = getDate(getMoment(index + 1));
      setIndex((r) => r + 1);
      setWeekIndex(1);
    } else {
      setWeekIndex(addEnd);
    }
  };
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  const previousWeek = () => {
    const sub = weekIndex - 1;
    if (sub < 0) {
      const weeks = getDate(getMoment(index - 1));
      weeksRef.current = weeks;
      setIndex((r) => r - 1);
      setWeekIndex(weeks.length - 2);
    } else {
      setWeekIndex(sub);
    }
  };
  /* eslint-enable react-hooks/exhaustive-deps */

  const today = () => {
    const weeks = getDate(getMoment(0));
    const date = dayjs();
    weeksRef.current = weeks;
    setIndex(0);
    weeks.forEach((week, idx) => {
      if (week.includes(date.format(INTERNAL_DATE_FORMAT))) {
        setWeekIndex(idx);
        return;
      }
    });
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const next = () => (isMonthView ? setIndex((r) => r + 1) : nextWeek());
  const previous = () =>
    isMonthView ? setIndex((r) => r - 1) : previousWeek();
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (localStorage.getItem(CHANGE_EVENT_LOCAL_KEY)) return;
    const idEventForFeedback = localStorage.getItem(FEEDBACK_EVENT_ID);

    if (
      idEventForFeedback &&
      eventMaterials &&
      eventMaterials.item.events?.length
    ) {
      const events = eventMaterials.item.events;
      const idEvent = parseInt(idEventForFeedback);
      const event = events?.find((e) => e?.id === idEvent);

      if (!event) {
        localStorage.removeItem(FEEDBACK_EVENT_ID);
        return;
      }

      const title = getTitle(event);
      const start = new Date(`${event?.data} ${event?.ora_fine}`);
      const type = event.type;
      const topic = event.descrizione;
      const studentWasPresent = event.user_events.some((ue) => ue.is_logged_in);

      let modifiedType = type;

      if (type.toLowerCase() === "speaking" && topic === "Mock test") {
        modifiedType = "MockTest";
      }

      const displayText =
        typeToDisplayText[modifiedType as keyof typeof typeToDisplayText] ||
        type;

      const textColorClass =
        typeToTextColor[modifiedType as keyof typeof typeToTextColor] ||
        typeToTextColor.default;

      let headingStyle = `text-14px font-semibold ${textColorClass}`;

      const headerStyle = typeToHeaderClass.default;
      headingStyle +=
        " " +
        (typeToHeaderClass[modifiedType as keyof typeof typeToHeaderClass] ||
          typeToHeaderClass.default);

      if (start <= new Date() && studentWasPresent) {
        openModal(
          "AGENDA_EVENT_FEEDBACK",
          {
            id_evento: idEvent,
            title,
            tutor_nome: event?.tutors?.first_name,
            date: dayjs(event.data),
            modifiedType,
          },
          {
            renderHeader: (
              <div
                className={cn(
                  "flex items-center px-2 h-10 bg-brand-secondary rounded-t-md",
                  headerStyle,
                )}
              >
                <h2
                  className={cn(
                    "text-sm font-semibold text-left text-light rounded-sm px-2",
                    headingStyle,
                  )}
                >
                  {displayText}
                </h2>
              </div>
            ),
          },
        );
      }
    }
  }, [eventMaterials, openModal]);

  const value = useMemo(
    () => ({
      previousWeek,
      nextWeek,
      today,
      week: weeksRef.current[weekIndex],
      isCurrentWeek: weekIndex === currentWeekIndex && index === 0,
      isCurrentMonth: index === 0,
      index,
      isMonthView,
      toggleMonthView,
      next,
      previous,
    }),
    [
      weekIndex,
      index,
      isMonthView,
      currentWeekIndex,
      next,
      nextWeek,
      previous,
      previousWeek,
      toggleMonthView,
    ],
  );

  return (
    <AgendaContext.Provider value={value}>{children}</AgendaContext.Provider>
  );
};

/* eslint-disable react-refresh/only-export-components */
export const useAgenda = () => {
  const context = useContext(AgendaContext);
  if (context === undefined) {
    throw new Error("useAgenda must be used within an AgendaProvider");
  }

  return context;
};
